import neo4j from 'neo4j-driver';

/**
 * Neo4j Connection Utility for Profile360
 * Provides secure connection management with environment variables
 */

/**
 * Validates Neo4j environment variables
 * @throws {Error} If required environment variables are missing
 */
const validateNeo4jConfig = () => {
  const NEO4J_URI = process.env.REACT_APP_NEO4J_URI;
  const NEO4J_USER = process.env.REACT_APP_NEO4J_USER;
  const NEO4J_PASSWORD = process.env.REACT_APP_NEO4J_PASSWORD;

  if (!NEO4J_URI || !NEO4J_USER || !NEO4J_PASSWORD) {
    throw new Error(
      'Neo4j credentials not configured. Please set REACT_APP_NEO4J_URI, REACT_APP_NEO4J_USER, and REACT_APP_NEO4J_PASSWORD environment variables.'
    );
  }

  return { NEO4J_URI, NEO4J_USER, NEO4J_PASSWORD };
};

/**
 * Creates a Neo4j driver instance
 * @returns {Object} Neo4j driver instance
 */
export const createNeo4jDriver = () => {
  const { NEO4J_URI, NEO4J_USER, NEO4J_PASSWORD } = validateNeo4jConfig();
  
  return neo4j.driver(
    NEO4J_URI,
    neo4j.auth.basic(NEO4J_USER, NEO4J_PASSWORD)
  );
};

/**
 * Executes a Cypher query with proper connection management
 * @param {string} query - Cypher query string
 * @param {Object} params - Query parameters
 * @returns {Promise<Array>} Query result records
 */
export const executeCypherQuery = async (query, params = {}) => {
  const driver = createNeo4jDriver();
  const session = driver.session();
  
  try {
    const result = await session.run(query, params);
    return result.records;
  } catch (err) {
    throw err;
  } finally {
    await session.close();
    await driver.close();
  }
};

/**
 * Executes a query and returns the session for transaction management
 * Use this for complex operations that need transaction control
 * @param {string} query - Cypher query string
 * @param {Object} params - Query parameters
 * @returns {Object} { result, session, driver } - Must manually close session and driver
 */
export const executeCypherQueryWithSession = async (query, params = {}) => {
  const driver = createNeo4jDriver();
  const session = driver.session();
  
  try {
    const result = await session.run(query, params);
    return { result, session, driver };
  } catch (err) {
    await session.close();
    await driver.close();
    console.error('Neo4j query error:', err);
    throw err;
  }
};

/**
 * Tests Neo4j connection
 * @returns {boolean} True if connection successful
 */
export const testNeo4jConnection = async () => {
  try {
    const driver = createNeo4jDriver();
    const session = driver.session();
    
    await session.run('RETURN 1');
    await session.close();
    await driver.close();
    
    return true;
  } catch (err) {

    return false;
  }
};
